import _, { findLastIndex } from 'lodash';
import React, { useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FormInputContainer, FormContext } from 'blaze-form';
import { Button, ButtonGroup, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ApiService from '../../services/ApiService';
import DealContext from './DealContext';
import PostToAccounting from './PostToAccounting';
import CancelQuoteForm from './CancelQuoteForm';
import { modal } from '../modal/ModalEmitter';
import toastr from '../toastr';
import { getSalesRoles } from './util';

const FormInputQuoteFlow = props => {
  const { manager, ee } = useContext(FormContext);
  const { deal, updateQuoteStatus } = useContext(DealContext);
  const user = useSelector(state => state.app.user);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(Number(manager.getValue('statusId')));
  const [states, setStates] = useState([]);
  const [settings, setSettings] = useState([]);
  const { isSalesPerson, isSalesManager, isSalesAccountant } = getSalesRoles(user);

  const quoteId = manager.itemId;

  const stateconstant = {};
  states.forEach(state => {
    stateconstant[state.logicalName] = state.id;
  });

  const isDead = status === stateconstant.CANCELLED || status === stateconstant.CLOSED;
  const approved = !(status === stateconstant.OPEN || status === stateconstant.AWAITING);

  const updateVehicleStatus = status => {
    const vehicleId = manager.getValue('vehicleId');

    return ApiService.put(`crm/vehicle/${vehicleId}/change-status`, { status }).then(() => {
      ee.emit('vehicle.status', { vehicleId, status });
    });
  };

  const getStates = () => {
    ApiService.get('/crm/states').then(response => {
      setStates(response.data);
    });
  };

  const fetchSettings = () => {
    return ApiService.get('/crm/settings').then(response => {
      setSettings(response.data);
    });
  };

  const handleReopen = () => {
    // if (!isDead) {
    //   console.error('Quote is still open');
    //   return;
    // }

    const nextStateId = stateconstant.OPEN;

    setLoading(true);
    modal.confirm({
      confirmLabels: ['Reopen', 'Cancel'],
      message: `Are you sure you want to reopen Quote ${quoteId}?  The attached unit will become Available and any posting to accounting will NOT be automatically reversed.`,
      callback: () => {
        return ApiService.put(`/db/EQS1/${quoteId}`, { statusId: nextStateId })
          .then(() => {
            updateStatus(nextStateId, { clearCache: true });
            updateVehicleStatus('A');
          })
          .finally(() => {
            setTimeout(() => {
              setLoading(false);
            }, 10);
          });
      },
      onClose: () => setLoading(false),
    });
  };

  const handleAccountingPost = () => {
    setLoading(true);

    const nextStateId = stateconstant.POSTED;

    const quoteId = manager.itemId;
    const dealId = manager.getValue('dealId');

    modal.open('PostToAccounting', {
      title: `Confirm Posting [Q${quoteId}] [Ref ${dealId}]`,
      style: 'center',
      hideFooter: true,
      hidePrint: true,
      staticBackdrop: true,
      message: (
        <PostToAccounting
          deal={deal}
          quoteId={quoteId}
          callback={() => {
            modal.close();
            updateStatus(nextStateId);
            updateVehicleStatus('S');
          }}
        />
      ),
      onClose: () => setLoading(false),
    });
  };

  const handleCancel = () => {
    const nextStateId = stateconstant.CANCELLED;
    const message = !approved ? (
      <div>
        <div className="info-sect" style={{ marginTop: '-10px' }}>
          <div>Since this quote was not approved it will be marked for deletion.</div>
        </div>
        Please confirm cancellation of <strong>Q{quoteId}</strong>.
      </div>
    ) : (
      <CancelQuoteForm />
    );

    setLoading(true);
    modal.confirm({
      message,
      title: 'Cancel Quote',
      confirmLabels: ['Confirm', 'Dismiss'],
      hideFooter: approved,
      callback: cancelReason => {
        return ApiService.put(`crm/quote/${quoteId}/cancel`, { reason: approved && cancelReason })
          .then(() => {
            updateStatus(nextStateId, { clearCache: true });
          })
          .finally(() => {
            setLoading(false);
          });
      },
      onClose: () => setLoading(false),
    });
  };

  const handleClick = () => {
    if (!status || !quoteId) {
      console.error('Invalid quoteId or statusId');
      return;
    }

    if (status === stateconstant.CANCELLED || status === stateconstant.CLOSED) {
      console.error('Already in terminal state');
      return;
    }

    const nextState = getNextState(status);

    if (!nextState || !nextState.id) {
      setLoading(false);
      return;
    }

    const nextStateId = nextState.id;

    if (nextStateId === stateconstant.APPROVED) {
      return handleApprove();
    }

    if (nextStateId === stateconstant.CANCELLED) {
      return; // Needs to go through handleCancel
    }

    setLoading(true);
    return ApiService.put(`/crm/quote/${quoteId}/change-status`, { statusId: nextStateId })
      .then(() => {
        updateStatus(nextStateId);
      })
      .finally(() => {
        setTimeout(() => {
          setLoading(false);
        }, 10);
      });
  };

  const handleApprove = () => {
    setLoading(true);

    return ApiService.put(`/crm/quote/${quoteId}/approve`, { statusId: stateconstant.APPROVED })
      .then(response => {
        updateStatus(stateconstant.APPROVED);
        const approvedBy = response.data.approvedBy;
        updateForm({ approvedBy: String(approvedBy) });
      })
      .catch(error => {
        toastr.error('Error', _.get(error, 'response.data.error', error.message));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateStatus = (newStatus, options) => {
    updateForm({ statusId: String(newStatus) });
    setStatus(newStatus);
    if (updateQuoteStatus) {
      updateQuoteStatus(quoteId, options);
    }
  };

  const updateForm = args => {
    if (!_.get(manager, 'formHandler.current.updateForm')) {
      return;
    }

    manager.formHandler.current.updateForm(args);
  };

  // Returning disabled status
  const canCancel = () => {
    const dirtyDeadOrLoading = manager.isDirty() || isDead || loading;

    if (dirtyDeadOrLoading) {
      return findLastIndex;
    }

    if (isSalesManager) {
      return true;
    }

    if (settings.quoteSalesPersonCancel === 'Y' && isSalesPerson) {
      const assigneeId = manager.getValue('assignedTo');
      if (Number(assigneeId) === user.id) {
        return true;
      }
    }

    return false;
  };

  const getNextState = statusId => {
    const orderedStates = _.orderBy(states, 'order');
    const currentIndex = orderedStates.findIndex(s => s.id === statusId);

    if (!orderedStates[currentIndex + 1] || currentIndex === -1) {
      return null;
    }

    return orderedStates[currentIndex + 1];
  };

  const getTransitionLabel = () => {
    if (loading) {
      return <i className="fad fa-spinner-third fa-spin" />;
    }

    const currentState = states.find(s => s.id === status);

    if (currentState && currentState.alive === false) {
      return 'Reopen';
    }

    const nextState = getNextState(status);

    if (!nextState || !nextState.transitionLabel) {
      return 'Next';
    }

    return nextState.transitionLabel;
  };

  useEffect(() => {
    if (status === stateconstant.APPROVED) {
      updateVehicleStatus('H');
    }
  }, [status]);

  useEffect(() => {
    getStates();
    setStatus(Number(manager.getValue('statusId')));
    fetchSettings();
  }, []);

  const transitionLabel = getTransitionLabel();

  const transitionButtonStatus = () => {
    if (loading) {
      return { tip: 'Loading', disabled: true };
    }

    if (isDead) {
      return { tip: 'Quote needs to be reopened', disabled: true };
    }

    if (manager.isDirty()) {
      return { tip: 'Quote needs to be saved first', disabled: true };
    }

    const requiresManagerApproval = settings.quoteSalesManagerApproval === 'Y';
    const grossProfitThreshold = settings.quoteGrossProfitApprovalThresholdPercent;
    const hasThreshold = grossProfitThreshold && Number(grossProfitThreshold) > 0;

    if (status === stateconstant.AWAITING) {
      if (requiresManagerApproval && !isSalesManager) {
        return { tip: 'Requires Sales Manager Role', disabled: true };
      }

      if (hasThreshold) {
        const grossProfitPercent = Number(manager.getValue('data.salesPriceGrossProfitPercent') || 0);
        if (parseFloat(grossProfitPercent) < parseFloat(grossProfitThreshold) && !isSalesManager) {
          return { tip: `Sales Manager Required: Gross Profit below ${grossProfitThreshold}`, disabled: true };
        }
      }
    }

    if (requiresManagerApproval && status === stateconstant.AWAITING && !isSalesManager) {
      return { tip: 'Requires Sales Manager Role', disabled: true };
    }

    if (status === stateconstant.BOOKED && !isSalesAccountant) {
      return { tip: 'Requires Sales Accountant Role', disabled: true };
    }

    if (status === stateconstant.BOOKED && isSalesAccountant) {
      return { disabled: false };
    }

    if (!isSalesManager && !isSalesPerson) {
      return { tip: 'Requires Sales Person or Sales Manager Roles', disabled: true };
    }

    return { disabled: false };
  };

  const { tip, disabled } = transitionButtonStatus();

  const transitionButton = (
    <Dropdown as={ButtonGroup} id="transition-button" className="transition-group">
      <Button
        disabled={disabled}
        onClick={status === stateconstant.BOOKED ? handleAccountingPost : handleClick}
        style={disabled ? { pointerEvents: 'none' } : {}}
      >
        {transitionLabel}
      </Button>
      <Dropdown.Toggle split />
      <Dropdown.Menu>
        {isSalesManager && (
          <Dropdown.Item eventKey="2" onClick={handleReopen}>
            Move Back To Open
          </Dropdown.Item>
        )}
        <Dropdown.Item eventKey="1" onClick={handleCancel} disabled={!canCancel()}>
          Cancel Quote
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  return (
    <FormInputContainer {...props}>
      {isDead === true ? (
        <Button block variant="success" onClick={handleReopen} disabled={loading}>
          {loading === true ? <i className="fad fa-spinner-third fa-spin" /> : 'Reopen'}
        </Button>
      ) : (
        <div className="d-flex">
          {tip ? (
            <OverlayTrigger
              placement="top"
              delay={{ show: 500, hide: 400 }}
              overlay={tip ? <Tooltip>{tip}</Tooltip> : <div />}
            >
              <div style={{ width: '100%' }}>{transitionButton}</div>
            </OverlayTrigger>
          ) : (
            transitionButton
          )}
        </div>
      )}
    </FormInputContainer>
  );
};

export default FormInputQuoteFlow;
